import { Group, Modal, Stack } from '@mantine/core';
import { useTranslation, Trans } from 'react-i18next';
import { baseWhite, inkLight, redBase, skyDark } from '../../../styles/design-tokens';
import { Button } from '../../../ui-library/button/button';
import { Divider } from '../../../ui-library/divider/divider';
import { Icon } from '../../../ui-library/icon/icon';
import { Text } from '../../../ui-library/typography/typography';
import type { StrategyEntityType } from '../strategy.type';

type Props = {
  opened: boolean;
  onClose: () => void;
  onDelete: () => void;
  isDeleting?: boolean;
  entityName?: string;
  entityType: StrategyEntityType;
};

export function StrategyEntityDeleteModal({
  opened,
  onClose,
  onDelete,
  isDeleting = false,
  entityName,
  entityType,
}: Props) {
  const i18n = useTranslation();
  const getEntityTitle = () => {
    return entityType === 'initiative'
      ? i18n.t('common.initiative')
      : entityType === 'outcome'
        ? i18n.t('common.outcome')
        : i18n.t('common.strategic_goal');
  };

  const getDeleteKey = () => {
    return entityType === 'initiative' ? 'initiative' : entityType === 'outcome' ? 'objective' : 'goal';
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size={580}
      padding={24}
      withCloseButton={false}
      styles={{
        content: {
          borderRadius: 8,
          boxShadow: 'var(--box-shadow-big-soft)',
        },
      }}
    >
      <Stack gap={24}>
        <Group justify="space-between" align="center">
          <Text size="large" weight="bold">
            Delete {getEntityTitle()}
          </Text>
          <Icon name="close" size={24} color={skyDark} onClick={onClose} pointer />
        </Group>
        <Divider />
        <Stack gap={16}>
          <Text size="medium">
            <Trans
              i18nKey="strategy.initiatives.delete.description_first"
              values={{ name: entityName || getDeleteKey() }}
              components={{ strong: <strong /> }}
            />
          </Text>
          <Text size="medium">{i18n.t('strategy.initiatives.delete.description_second')}</Text>
        </Stack>
        <Divider />
        <Group justify="flex-end" gap={16}>
          <Button
            data-bf="strategy-modal-btn-cancel-delete"
            radius="xl"
            variant="outline"
            onClick={onClose}
            disabled={isDeleting}
            style={{ borderColor: inkLight }}
          >
            <Text weight="medium" style={{ color: inkLight }}>
              {i18n.t('strategy.initiatives.form.cancel')}
            </Text>
          </Button>
          <Button
            data-bf="strategy-modal-btn-confirm-delete"
            radius="xl"
            onClick={onDelete}
            loading={isDeleting}
            disabled={isDeleting}
            style={{ backgroundColor: redBase }}
            rightSection={<Icon variant="filled" name="delete" color={baseWhite} size={16} pointer />}
          >
            <Text weight="medium" style={{ color: baseWhite }}>
              {i18n.t('strategy.initiatives.delete.confirm')}
            </Text>
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
